import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  PARTNER_DRIVERS_GRID_COLUMNS_STORAGE_KEY,
  partnerDriverPageSizeOptions,
} from 'src/constants/partnerDriver/constants'
import { PartnerDriversTableFields } from 'src/constants/partnerDriver/enums'
import { partnerDriverColumns } from 'src/constants/partnerDriver/partnerDriverGrid'
import type { PartnerDriverSearchRow } from 'src/interfaces/partnerDriver'
import {
  getPartnerDriverSearchLoading,
  getPartnerDriverSearchResults,
} from 'src/selectors/partnerDriver'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { EmptyPartnerDrivers } from './EmptyPartnerDrivers'

interface Props {
  hasSearchQuery: boolean
  onOpenDriver(row: PartnerDriverSearchRow): void
}

const EMPTY_ROWS: PartnerDriverSearchRow[] = []

const UNHIDEABLE_COLUMNS: string[] = [PartnerDriversTableFields.name]

const getRowId = (row: PartnerDriverSearchRow) => {
  return row.id
}

const PartnerDriversTableComponent = ({ hasSearchQuery, onOpenDriver }: Props) => {
  const searchResults = useSelector(getPartnerDriverSearchResults)
  const isLoading = useSelector(getPartnerDriverSearchLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(partnerDriverPageSizeOptions)
  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    partnerDriverColumns,
    PARTNER_DRIVERS_GRID_COLUMNS_STORAGE_KEY,
  )

  // The slice keeps the last results across pages; with no query in the URL they are not this
  // view's results, so they are not shown.
  const rows = hasSearchQuery ? searchResults : EMPTY_ROWS

  const columnVisibilityModel = useMemo(() => {
    return partnerDriverColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [includedColumns])

  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      partnerDriverColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const handleRowClick = useCallback(
    (params: GridRowParams<PartnerDriverSearchRow>) => {
      onOpenDriver(params.row)
    },
    [onOpenDriver],
  )

  // `GridTable` renders EmptyElement with no props, so the query-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyPartnerDrivers = () => {
      return <EmptyPartnerDrivers hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyPartnerDrivers
  }, [hasSearchQuery])

  return (
    <GridTable<PartnerDriverSearchRow>
      rows={rows}
      columns={partnerDriverColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={partnerDriverPageSizeOptions}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      onRowClick={handleRowClick}
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      EmptyElement={EmptyElement}
      tableDataTestId="partner-drivers-list-table"
      rowDataTestIdPrefix="partner-drivers-list"
    />
  )
}

export const PartnerDriversTable = memo(PartnerDriversTableComponent)
